import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, Edit2, Trash2, Mail, Phone, Calendar, CreditCard,
  Car, FileText, Eye, Upload, AlertTriangle, CheckCircle, Clock,
  User, Shield, ReceiptText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDrivers, type Driver } from "@/hooks/useVehicles";
import { DriverDialog } from "@/components/fleet/DriverDialog";
import { FineCard } from "@/components/fines/FineCard";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { SEO } from "@/components/seo/SEO";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { UnifiedDocumentLinkDialog } from "@/components/shared/UnifiedDocumentLinkDialog";
import type { TrafficFine } from "@/hooks/useTrafficFines";

export default function DriverDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { deleteDriver, updateDriver } = useDrivers();
  const { openDocument } = useDocumentPreview();
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

  // Fetch driver with license document
  const { data: driver, isLoading, error } = useQuery({
    queryKey: ["driver", id],
    queryFn: async () => {
      if (!id || !user?.id) return null;

      const { data, error } = await supabase
        .from("drivers")
        .select(`
          *,
          license_document:documents!drivers_license_document_id_fkey(id, title, file_path, mime_type)
        `)
        .eq("id", id)
        .eq("user_id", user.id)
        .single();

      if (error) throw error;
      return data as Driver & { 
        license_document: { id: string; title: string; file_path: string; mime_type: string } | null 
      };
    },
    enabled: !!id && !!user?.id,
  });

  // Fetch vehicle assignments
  const { data: vehicleAssignments = [] } = useQuery({
    queryKey: ["driver-vehicles", id],
    queryFn: async () => {
      if (!id) return [];

      const { data, error } = await supabase
        .from("vehicle_drivers")
        .select(`
          *,
          vehicle:vehicles(*)
        `)
        .eq("driver_id", id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
  });

  // Fetch traffic fines
  const { data: fines = [] } = useQuery({
    queryKey: ["driver-fines", id],
    queryFn: async () => {
      if (!id) return [];

      const { data, error } = await supabase
        .from("traffic_fines")
        .select("*, vehicle:vehicles(license_plate, brand, model)")
        .eq("driver_id", id)
        .order("offense_date", { ascending: false });

      if (error) throw error;
      return (data || []) as (TrafficFine & { vehicle: { license_plate: string; brand: string; model: string } | null })[];
    },
    enabled: !!id,
  });

  // License status calculation
  const getLicenseStatus = () => {
    if (!driver?.license_expiry) return { status: "unknown", label: "Non renseigné", variant: "outline" as const };
    
    const expiryDate = new Date(driver.license_expiry);
    const daysUntilExpiry = differenceInDays(expiryDate, new Date());
    
    if (isPast(expiryDate)) {
      return { status: "expired", label: "Expiré", variant: "destructive" as const };
    }
    if (daysUntilExpiry <= 30) {
      return { status: "expiring", label: `Expire dans ${daysUntilExpiry}j`, variant: "secondary" as const };
    }
    return { status: "valid", label: "Valide", variant: "default" as const };
  };

  const licenseStatus = getLicenseStatus();

  const statusIcon = {
    expired: <AlertTriangle className="h-4 w-4" />,
    expiring: <Clock className="h-4 w-4" />,
    valid: <CheckCircle className="h-4 w-4" />,
    unknown: <Shield className="h-4 w-4" />,
  };

  // Use standard DocumentPreviewSheet via context
  const handleViewLicense = async () => {
    if (!driver?.license_document) return;
    
    try {
      // Fetch the full document from the documents table
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", driver.license_document.id)
        .single();

      if (error || !data) {
        toast.error("Impossible de charger le document");
        return;
      }

      openDocument(data);
    } catch (err) {
      console.error("[DriverDetail] Error loading license:", err);
      toast.error("Erreur lors du chargement");
    }
  };

  const handleUpdate = async (data: Omit<Driver, 'id' | 'user_id' | 'created_at' | 'access_token'>) => {
    if (!id || !driver) return;
    await updateDriver.mutateAsync({ 
      id, 
      ...data,
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteDriver.mutateAsync(id);
    navigate("/fleet");
  };

  // License document config for UnifiedDocumentLinkDialog
  const licenseDocConfig = {
    key: "license",
    label: "Permis de Conduire",
    doc_type: "identity",
    validityDays: 365 * 15, // 15 years for French license
  };

  if (isLoading) {
    return (
      <div className="container max-w-5xl py-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="container max-w-5xl py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">Conducteur non trouvé</h3>
            <p className="text-muted-foreground mb-4">
              Ce conducteur n'existe pas ou vous n'y avez pas accès.
            </p>
            <Button onClick={() => navigate("/fleet")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à la flotte
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initials = `${driver.first_name.charAt(0)}${driver.last_name.charAt(0)}`.toUpperCase();

  return (
    <>
      <SEO 
        title={`${driver.first_name} ${driver.last_name} - EasyFinder Fleet`}
        description={`Dossier du conducteur ${driver.first_name} ${driver.last_name}`}
      />

      <div className="container max-w-5xl py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="space-y-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/fleet")}
              className="-ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Flotte
            </Button>

            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="text-xl bg-primary/10 text-primary font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold">
                  {driver.first_name} {driver.last_name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={licenseStatus.variant} className="gap-1">
                    {statusIcon[licenseStatus.status]}
                    Permis {licenseStatus.label}
                  </Badge>
                  {driver.status && (
                    <Badge variant="outline" className="capitalize">
                      {driver.status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Mail className="h-4 w-4" />
                <span>{driver.email}</span>
              </div>
              {driver.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  <span>{driver.phone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Modifier
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce conducteur ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. Le conducteur sera détaché de tous les véhicules.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* License Document Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Permis de Conduire
                </CardTitle>
              </CardHeader>
              <CardContent>
                {driver.license_document ? (
                  <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/30">
                    <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground mb-4 text-center">
                      {driver.license_document.title}
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={handleViewLicense} className="gap-2">
                        <Eye className="h-4 w-4" />
                        Voir le permis
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setLicenseDialogOpen(true)}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Remplacer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors border-muted-foreground/25 hover:border-primary/50"
                    onClick={() => setLicenseDialogOpen(true)}
                  >
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium mb-1">Aucun permis scanné</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Cliquez pour ajouter ou lier un document existant
                    </p>
                    <Button variant="outline">
                      Ajouter un permis
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="vehicles">
              <TabsList>
                <TabsTrigger value="vehicles" className="gap-2">
                  <Car className="h-4 w-4" />
                  Véhicules ({vehicleAssignments.length})
                </TabsTrigger>
                <TabsTrigger value="fines" className="gap-2">
                  <ReceiptText className="h-4 w-4" />
                  Infractions ({fines.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="vehicles" className="mt-4">
                {vehicleAssignments.length > 0 ? (
                  <div className="grid gap-4">
                    {vehicleAssignments.map((assignment) => (
                      <Card 
                        key={assignment.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/fleet/${assignment.vehicle?.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="bg-muted px-3 py-1.5 rounded-md border font-mono font-semibold">
                                {assignment.vehicle?.license_plate}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {assignment.vehicle?.brand} {assignment.vehicle?.model}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Assigné depuis le {format(new Date(assignment.assigned_at), "d MMM yyyy", { locale: fr })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {assignment.is_primary && (
                                <Badge variant="secondary">Principal</Badge>
                              )}
                              <Badge variant={assignment.vehicle?.status === "active" ? "default" : "outline"}>
                                {assignment.vehicle?.status === "active" ? "Actif" : assignment.vehicle?.status}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <Car className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">
                        Aucun véhicule assigné à ce conducteur
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="fines" className="mt-4">
                {fines.length > 0 ? (
                  <div className="space-y-3">
                    {fines.map((fine) => (
                      <FineCard 
                        key={fine.id} 
                        fine={fine}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <ReceiptText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">
                        Aucune infraction enregistrée
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* License info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Informations Permis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {driver.license_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Numéro</span>
                    <span className="font-mono">{driver.license_number}</span>
                  </div>
                )}
                
                {driver.license_categories && driver.license_categories.length > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Catégories</span>
                    <div className="flex gap-1">
                      {driver.license_categories.map((cat) => (
                        <Badge key={cat} variant="outline" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {driver.license_expiry && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expiration</span>
                    <span>{format(new Date(driver.license_expiry), "d MMMM yyyy", { locale: fr })}</span>
                  </div>
                )}

                <Separator />

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Véhicules</span>
                  <span className="font-semibold">{vehicleAssignments.length}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Infractions</span>
                  <span className="font-semibold">{fines.length}</span>
                </div>
              </CardContent>
            </Card>

            {/* Created date */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Créé le {format(new Date(driver.created_at), "d MMMM yyyy", { locale: fr })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit dialog */}
        <DriverDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          driver={driver}
          onSubmit={handleUpdate}
        />

        {/* License upload dialog */}
        {id && (
          <UnifiedDocumentLinkDialog
            open={licenseDialogOpen}
            onOpenChange={setLicenseDialogOpen}
            entityType="driver"
            entityId={id}
            docConfig={licenseDocConfig}
            linkTableName="drivers"
            showValidityDates={true}
            defaultTab="upload"
          />
        )}
      </div>
    </>
  );
}
